/**
 * Shared GA4 instrumentation for the two PDP upsell widgets while they run as a
 * 50/50 A/B test.
 *
 * The widgets are tracked as two separate populations through the `widget`
 * param, so their engagement rates never mix:
 *   - 'new_upsell'     the bundle component, in BOTH of its modes ('deal' and
 *                      'chains') - it is the same UI either way, so switching
 *                      to chains still counts towards the new component
 *   - 'legacy_chains'  the old chain cross-sell
 *
 * Two events, each sent at most once per page view:
 *   upsell_view     widget was at least half visible for one second
 *   upsell_engage   first interaction of any kind, carries `action`
 *
 * Engagement rate = Sessions(upsell_engage) / Sessions(upsell_view), split by
 * `widget`. Session-level dedup is deliberately left to GA4's session grouping
 * rather than sessionStorage, whose lifetime (tab close) does not line up with
 * a GA4 session (30 min idle, or midnight).
 *
 * This file owns view tracking for both widgets: it finds them itself on DOM
 * ready, which keeps it independent of how each widget's own script is loaded
 * (one is defer, the other async). Widget scripts only report interactions,
 * via MellerUpsellAnalytics.engage().
 */
(function () {
  var VIEW_THRESHOLD = 0.5; // fraction of the widget that must be visible
  var VIEW_DWELL_MS = 1000; // how long it must stay visible
  var SCROLL_MIN_PX = 30; // ignore inertia from vertical page scrolling

  var WIDGETS = [
    {
      selector: '.product-upsell',
      params: function (el) {
        return {
          widget: 'new_upsell',
          upsell_mode: el.getAttribute('data-upsell-mode') || 'deal',
          promo: el.getAttribute('data-promo') || undefined,
          product_id: el.getAttribute('data-product-id') || undefined,
        };
      },
    },
    {
      selector: '#cross_selling',
      params: function (el) {
        return {
          widget: 'legacy_chains',
          promo: el.getAttribute('data-promo') || undefined,
          product_id: el.getAttribute('data-product-id') || undefined,
        };
      },
    },
  ];

  function send(name, params) {
    if (typeof gtag !== 'function') return;
    gtag('event', name, params);
  }

  function withTheme(base, extra) {
    var out = { theme_id: (window.Shopify && window.Shopify.theme && window.Shopify.theme.id) || undefined };
    var k;
    for (k in base) {
      if (base.hasOwnProperty(k)) out[k] = base[k];
    }
    for (k in extra || {}) {
      if (extra.hasOwnProperty(k)) out[k] = extra[k];
    }
    return out;
  }

  // Viewability rather than mere presence in the DOM: both widgets sit below the
  // variant picker, so plenty of visitors never reach them. Counting those would
  // inflate the denominator, by different amounts for each widget since they are
  // not the same height.
  function watchView(el, base) {
    if (!('IntersectionObserver' in window)) {
      send('upsell_view', withTheme(base));
      return;
    }
    var sent = false;
    var timer = null;
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !sent) {
            timer = setTimeout(function () {
              if (sent) return;
              sent = true;
              send('upsell_view', withTheme(base));
              observer.disconnect();
            }, VIEW_DWELL_MS);
          } else if (timer) {
            clearTimeout(timer);
            timer = null;
          }
        });
      },
      { threshold: VIEW_THRESHOLD }
    );
    observer.observe(el);
  }

  function attach(el, base) {
    el._upsellTracking = {
      base: base,
      engaged: false,
      scrollOrigin: null,
    };
    watchView(el, base);
  }

  function init() {
    WIDGETS.forEach(function (widget) {
      Array.prototype.forEach.call(document.querySelectorAll(widget.selector), function (el) {
        if (el._upsellTracking) return;
        attach(el, widget.params(el));
      });
    });
  }

  window.MellerUpsellAnalytics = {
    // Any interaction counts, but `action` is recorded so intent-driven clicks
    // can later be separated from plain carousel swiping.
    engage: function (el, action) {
      if (!el) return;
      var state = el._upsellTracking;
      if (!state || state.engaged) return;
      state.engaged = true;
      send('upsell_engage', withTheme(state.base, { action: action }));
    },
    // Carousel swiping, gated by a minimum distance so inertia from vertical
    // page scrolling does not register as an interaction.
    scrolled: function (el, position) {
      if (!el) return;
      var state = el._upsellTracking;
      if (!state || state.engaged) return;
      if (state.scrollOrigin === null) {
        state.scrollOrigin = position;
        return;
      }
      if (Math.abs(position - state.scrollOrigin) >= SCROLL_MIN_PX) {
        this.engage(el, 'scroll');
      }
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
